import { ReactElement } from 'react';
import {
  Button,
  makeStyles,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Replay } from '@mui/icons-material';
import { FontFamily } from '@principal-developer-portal/ui';
import { useAtom } from 'jotai';
import { adminShowIssuesAtom } from '../../../hooks/use-filters';

export interface MarkAsRemediatedButtonProps {
  canRemediate: boolean;
  isGrouped: boolean;
  isRemediated: boolean;
  onMarkAsRemediated: () => void;
  onUndoRemediated: () => void;
  loading?: boolean;
  remediatedBy?: string;
  remediatedAt?: string;
}

const useStyles = makeStyles(theme => ({
  button: {
    textTransform: 'none',
    fontFamily: FontFamily.Bold,
    fontWeight: 700,
    fontSize: '16px',
    padding: '8px 24px',
    borderRadius: '24px',
    border: `2px solid ${
      theme.palette.type === 'dark' ? theme.palette.link : '#003865'
    }`,
    color: theme.palette.type === 'dark' ? theme.palette.link : '#003865',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0, 56, 101, 0.04)',
    },
    '&:disabled': {
      opacity: 0.5,
      border: `2px solid ${
        theme.palette.type === 'dark' ? theme.palette.grey[500] : '#999'
      }`,
      color: theme.palette.type === 'dark' ? theme.palette.grey[500] : '#999',
    },
  },
  undoButton: {
    textTransform: 'none',
    fontFamily: FontFamily.Bold,
    fontWeight: 700,
    fontSize: '14px',
    padding: '6px 20px',
    borderRadius: '24px',
    border: `2px solid ${
      theme.palette.type === 'dark' ? theme.palette.link : '#003865'
    }`,
    color: theme.palette.type === 'dark' ? theme.palette.link : '#003865',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0, 56, 101, 0.04)',
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  remediatedInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  disabledMessage: {
    fontSize: '14px',
    color: theme.palette.type === 'dark' ? theme.palette.grey[400] : '#666',
    fontStyle: 'normal',
  },
}));

// eslint-disable-next-line max-lines-per-function
export const MarkAsRemediatedButton = (
  props: MarkAsRemediatedButtonProps,
): ReactElement => {
  const {
    canRemediate,
    isGrouped,
    isRemediated,
    onMarkAsRemediated,
    onUndoRemediated,
    loading,
    remediatedBy,
    remediatedAt,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const [adminShowIssues] = useAtom(adminShowIssuesAtom);
  const iconColor =
    theme.palette.type === 'dark' ? theme.palette.link : '#003865';

  if (isRemediated) {
    return (
      <div className={classes.container}>
        <div className={classes.remediatedInfo}>
          <TaskAltIcon style={{ color: iconColor, fontSize: '24px' }} />
          <Typography style={{ fontSize: '14px' }}>
            <strong>Marked as remediated</strong>
            {remediatedBy && ` by ${remediatedBy}`}
            {remediatedAt && ` at ${remediatedAt}`}
          </Typography>
        </div>
        <Button
          className={classes.undoButton}
          onClick={onUndoRemediated}
          disabled={loading || !canRemediate}
          startIcon={<Replay style={{ fontSize: '18px' }} />}
          data-testid="undo-mark-as-remediated-button"
        >
          Undo mark as remediated
        </Button>
      </div>
    );
  }

  // Hide when neither canRemediate nor adminShowIssues is true
  if (!canRemediate && !adminShowIssues) {
    return <></>;
  }

  const disabled = isGrouped || loading;

  const disabledIconColor =
    theme.palette.type === 'dark' ? theme.palette.grey[500] : '#999';

  const button = (
    <Button
      className={classes.button}
      onClick={onMarkAsRemediated}
      disabled={disabled}
      startIcon={
        <TaskAltIcon
          style={{
            color: disabled ? disabledIconColor : iconColor,
          }}
        />
      }
      data-testid="mark-as-remediated-button"
    >
      Mark as remediated
    </Button>
  );

  const renderButton = (): ReactElement => {
    if (isGrouped) {
      return (
        <>
          <Tooltip title="'Marking as remediated' is not available for grouped findings">
            <span>{button}</span>
          </Tooltip>
          <Typography className={classes.disabledMessage}>
            Marking as remediated is not available for grouped findings.
          </Typography>
        </>
      );
    }
    return button;
  };

  return <div className={classes.container}>{renderButton()}</div>;
};
